import {
  foldSmartKeyState,
  SmartKeyMode,
  SmartKeyState,
  SmartKeyVariant,
} from "./SmartKeyState";
import { constNull, pipe } from "fp-ts/function";
import {
  Button,
  InlineLoader,
  Space,
  Stack,
  Box,
  QRCode,
  FileContent,
  LocalizedString,
  useIsMobileLayout,
} from "@dbn/omnichannel-design-system-g4p";

import { RuntimeLocaleKey, useFormatMessage } from "../intl";
import { PushNotificationTimer } from "./PushNotificationTimer";
import { CheckPushAPI, ErrorCode } from "./api";
import { TaskEither } from "fp-ts/TaskEither";
import { Option } from "fp-ts/Option";

type Props = {
  variant: SmartKeyVariant;
  state: SmartKeyState;
  onStart: () => unknown;
  onCancel: () => unknown;
  onTimeout: () => unknown;
  onError: (errorCode: Option<ErrorCode>) => unknown;
  onSuccess: (mode: SmartKeyMode) => unknown;
  checkPushStatus: CheckPushAPI;
  generateQRCode: TaskEither<unknown, void>;
};

function StartButton(props: {
  variant: SmartKeyVariant;
  onClick: () => unknown;
}) {
  const formatMessage = useFormatMessage();
  const label = ((): LocalizedString => {
    switch (props.variant) {
      case "authorization":
        return formatMessage("SmartKey.startButtonLabel.authorization");
      case "authentication":
        return formatMessage("SmartKey.startButtonLabel.authentication");
    }
  })();
  return (
    <Button
      size="default"
      variant="primary"
      label={label}
      action={props.onClick}
    />
  );
}

function OperationInProgressLoader() {
  const formatMessage = useFormatMessage();
  return (
    <InlineLoader
      size="small"
      message={formatMessage("SmartKey.operationInProgress")}
    />
  );
}

function DisplayQRCode(props: { code: FileContent }) {
  const formatMessage = useFormatMessage();
  return (
    <Box column vAlignContent="center" hAlignContent="center">
      <QRCode
        code={props.code}
        size={180}
        ariaLabelMessage={formatMessage(
          "SmartKey.qrCode.ariaLabel" as RuntimeLocaleKey
        )}
      />
    </Box>
  );
}

export function Right(props: Props) {
  const formatMessage = useFormatMessage();
  const isMobileLayout = useIsMobileLayout();

  return pipe(
    props.state,
    foldSmartKeyState({
      Initial: () => (
        <Box
          grow
          column
          hAlignContent={isMobileLayout ? "center" : "right"}
          vAlignContent={isMobileLayout ? "center" : "bottom"}
          style={(!isMobileLayout && { height: "100%" }) || undefined}
        >
          <StartButton variant={props.variant} onClick={props.onStart} />
        </Box>
      ),
      SendingNotification: () => <OperationInProgressLoader />,
      WaitingForNotificationConfirmation: data => (
        <Stack
          column
          {...(!isMobileLayout && { fluid: true })}
          height={isMobileLayout ? undefined : "100%"}
        >
          <Stack column units={5}>
            <OperationInProgressLoader />
            <PushNotificationTimer
              {...data}
              onExpire={props.onTimeout}
              onError={props.onError}
              onSuccess={() => props.onSuccess("push")}
              checkPushStatus={props.checkPushStatus}
            />
          </Stack>
          <Space units={5} />
          <Button
            size="default"
            variant={isMobileLayout ? "text" : "secondary"}
            action={props.onCancel}
            label={formatMessage("SmartKey.cancelProcess")}
          />
        </Stack>
      ),
      SmartKeyGenericError: () => (
        <StartButton variant={props.variant} onClick={props.onStart} />
      ),
      GeneratingQRCode: () => <OperationInProgressLoader />,
      DisplayingQRCode: s => <DisplayQRCode code={s.QRCode} />,
      ValidatingQRCode: s => <DisplayQRCode code={s.QRCode} />,
      QRCodePINWrong: s => <DisplayQRCode code={s.QRCode} />,
      UserBlocked: constNull,
    })
  );
}
